import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect, useMemo } from 'react'
import { faComments, faSignOutAlt } from '@fortawesome/free-solid-svg-icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { logout } from '../../auth'
import { NAVIGATION_SECTIONS } from '../../app/navigationConfig'
import usePermissions from '../../hooks/usePermissions'

const Sidebar = ({ setMessages, collapsed, setSidebarCollapsed }) => {
  const [showText, setShowText] = useState(!collapsed)
  const navigate = useNavigate()
  const location = useLocation()
  const { hasPermission } = usePermissions()

  const filteredSections = useMemo(() => {
    return NAVIGATION_SECTIONS
      .map((section) => ({
        ...section,
        items: section.items.filter(
          (item) => !item.requiredPermission || hasPermission(item.requiredPermission)
        )
      }))
      .filter((section) => section.items.length > 0)
  }, [hasPermission])

  useEffect(() => {
    if (collapsed) {
      setShowText(false)
    } else {
      const timer = setTimeout(() => {
        setShowText(true)
      }, 200)

      return () => clearTimeout(timer)
    }
  }, [collapsed])

  const NavItem = ({ item, isActive }) => {
    return (
      <div
        className={`flex items-center ${
          collapsed ? 'px-3 py-3 mx-1 justify-center' : 'px-4 py-3 mx-3'
        } rounded-xl cursor-pointer transition-all duration-300 ${
          isActive
            ? 'bg-gradient-to-r from-light_coral to-primary text-white shadow-lg transform scale-105'
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dk-hover hover:text-gray-900 dark:hover:text-white hover:shadow-md hover:transform hover:scale-102'
        }`}
        onClick={() => {
          if (location.pathname === item.path) {
            navigate('/lobbies')
          } else {
            if (item.collapseOnSelect) {
              setSidebarCollapsed(true)
            }
            navigate(item.path)
          }
        }}
        title={collapsed ? item.name : ''}
      >
        <FontAwesomeIcon icon={item.icon} className={`w-5 h-5 ${collapsed ? '' : 'mr-3'}`} />
        {showText && (
          <span
            className={`text-sm font-medium transition-opacity duration-300 ${
              showText ? 'opacity-100' : 'opacity-0'
            }`}
          >
            {item.name}
          </span>
        )}
      </div>
    )
  }

  return (
    <div
      className={`h-screen ${
        collapsed ? 'w-16' : 'w-64'
      } bg-white dark:bg-dk-surface shadow-xl dark:shadow-black/30 dark:border-r dark:border-dk-border flex flex-col transition-all duration-300`}
    >
      {/* Header */}
      <div
        className={`flex items-center ${collapsed ? 'px-2 justify-center' : 'px-6'} h-[4rem] mb-4`}
      >
        <div className='flex items-center'>
          <img src={require('../../assets/icons/logo.png')} alt='logo' className='w-8 h-8' />
          {showText && (
            <span
              className={`ml-3 text-xl font-semibold text-gray-800 dark:text-gray-100 transition-opacity duration-300 ${
                showText ? 'opacity-100' : 'opacity-0'
              }`}
            >
              Admin Panel
            </span>
          )}
        </div>
      </div>

      {/* Chat Button */}
      {hasPermission('channels') && <div className={`${collapsed ? 'px-2' : 'px-4'} mb-6`}>
        <div
          className={`w-full bg-gradient-to-r from-primary to-light_coral hover:from-light_coral hover:to-primary text-white rounded-xl py-3 ${
            collapsed ? 'px-2' : 'px-4'
          } cursor-pointer transition-all duration-300 flex items-center justify-center shadow-lg hover:shadow-xl transform hover:scale-105`}
          onClick={() => setMessages(true)}
          title={collapsed ? 'Chat' : ''}
        >
          <FontAwesomeIcon icon={faComments} className={`w-5 h-5 ${collapsed ? '' : 'mr-2'}`} />
          {showText && (
            <span
              className={`font-medium transition-opacity duration-300 ${
                showText ? 'opacity-100' : 'opacity-0'
              }`}
            >
              CHAT
            </span>
          )}
        </div>
      </div>}

      {/* Navigation */}
      <nav className='flex-1 overflow-y-auto px-2'>
        {filteredSections.map((section, index) => (
          <div key={index} className='mb-6'>
            {showText && (
              <h3
                className={`px-4 py-2 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider transition-opacity duration-300 ${
                  showText ? 'opacity-100' : 'opacity-0'
                }`}
              >
                {section.title}
              </h3>
            )}
            <div className='space-y-2'>
              {section.items.map((item, itemIndex) => (
                <NavItem
                  key={itemIndex}
                  item={item}
                  isActive={location.pathname === item.path}
                />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Logout Button */}
      <div className='p-4'>
        <div
          className={`flex items-center ${
            collapsed ? 'px-3 py-3 justify-center' : 'px-4 py-3'
          } text-gray-500 dark:text-gray-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 rounded-xl cursor-pointer transition-all duration-300 hover:shadow-md hover:transform hover:scale-105`}
          onClick={() => logout({ returnTo: window.origin })}
          title={collapsed ? 'Cerrar sesión' : ''}
        >
          <FontAwesomeIcon icon={faSignOutAlt} className={`w-5 h-5 ${collapsed ? '' : 'mr-3'}`} />
          {showText && (
            <span
              className={`text-sm font-medium transition-opacity duration-300 ${
                showText ? 'opacity-100' : 'opacity-0'
              }`}
            >
              Cerrar sesión
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

export default Sidebar
